import { useEffect, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Autocomplete,
	Box,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	type SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	ROLLSET_SEARCH_MIN_LENGTH,
	useRollsetSearch,
} from "../hooks/useRollsCartridge";
import type {
	CartridgeOption,
	RollsetSearchResult,
} from "../models/RollsCartridge";

interface RollsCartridgeToolbarProps {
	rollset: RollsetSearchResult | null;
	onRollsetChange: (value: RollsetSearchResult | null) => void;

	cartridgeId: number | null;
	onCartridgeChange: (value: number | null) => void;
	cartridges: CartridgeOption[];

	onReset: () => void;
	isLoading: boolean;
}

const cartridgeLabel = (c: CartridgeOption): string =>
	[c.StandName, c.MillSizeName, c.Name, c.StatusName]
		.filter(Boolean)
		.join(" - ");

const rollsetLabel = (rs: RollsetSearchResult): string => rs.Name;

export const RollsCartridgeToolbar = ({
	rollset,
	onRollsetChange,
	cartridgeId,
	onCartridgeChange,
	cartridges,
	onReset,
	isLoading,
}: RollsCartridgeToolbarProps) => {
	const [searchInput, setSearchInput] = useState("");
	const { data: searchResults = [], isFetching: isSearching } =
		useRollsetSearch(searchInput);

	// The cartridge filter and the reset button both drop the selected rollset.
	// The typed text is controlled here, so it has to follow.
	useEffect(() => {
		if (rollset === null) setSearchInput("");
	}, [rollset]);

	const handleCartridgeChange = (event: SelectChangeEvent<number | string>) => {
		const value = event.target.value;
		onCartridgeChange(value === "" ? null : Number(value));
	};

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				px: 1.5,
				py: 0.75,
			}}
		>
			<Autocomplete
				options={searchResults}
				getOptionLabel={rollsetLabel}
				value={rollset}
				onChange={(_, value) => onRollsetChange(value)}
				inputValue={searchInput}
				onInputChange={(_, value) => setSearchInput(value)}
				loading={isSearching}
				isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
				renderOption={(props, rs) => (
					<li {...props} key={rs.Id}>
						<Box>
							<Typography sx={{ fontSize: 13, fontWeight: 600 }}>
								{rs.Name}
							</Typography>
							<Typography sx={{ fontSize: 11, color: "#888" }}>
								{[rs.TopRollName, rs.BottomRollName]
									.filter(Boolean)
									.join(" / ")}
								{rs.StatusName ? ` (${rs.StatusName})` : ""}
							</Typography>
						</Box>
					</li>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Rollset"
						placeholder="Rollset or roll number"
						size="small"
					/>
				)}
				noOptionsText={
					searchInput.length >= ROLLSET_SEARCH_MIN_LENGTH
						? "No rollsets found"
						: "Type to search"
				}
				filterOptions={(x) => x}
				sx={{ minWidth: 280 }}
			/>

			<FormControl size="small" sx={{ minWidth: 280 }}>
				<InputLabel id="rolls-cartridge-cartridge-label">Cartridge</InputLabel>
				<Select
					labelId="rolls-cartridge-cartridge-label"
					label="Cartridge"
					value={cartridgeId ?? ""}
					onChange={handleCartridgeChange}
				>
					<MenuItem value="">
						<em>Select a cartridge</em>
					</MenuItem>
					{cartridges.map((c) => (
						<MenuItem key={c.Id} value={c.Id}>
							{cartridgeLabel(c)}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<Tooltip title="Clear the filters and refresh">
				<span>
					<IconButton
						onClick={() => {
							setSearchInput("");
							onReset();
						}}
						disabled={isLoading}
						size="small"
						sx={{
							bgcolor: "primary.main",
							color: "white",
							width: 32,
							height: 32,
							"&:hover": { bgcolor: "primary.dark" },
							"&.Mui-disabled": {
								bgcolor: "action.disabledBackground",
								color: "action.disabled",
							},
						}}
					>
						<RefreshIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);
};
